import type { SearchableSelectOption } from '@/components/ui/SearchableSelect'
import type { PropertyStaffRow, TransferProgressJson, TransferTabId, TransferWizardFormValues } from '../types'
import type { UseFormGetValues } from 'react-hook-form'

/** Overrides for persisted wizard flags when React state has not flushed yet (e.g. right after staff commit). */
export type TransferProgressFlagOverrides = Partial<{
  commitDone: boolean
  verifyDone: boolean
  empDone: boolean
  infoDone: boolean
  newPropertySave: string
  residentDone: boolean
  residentcount: number
  applicantcount: number
}>

export function buildTransferProgressPayload(params: {
  progressData: TransferProgressJson
  selectedTab: TransferTabId
  infoDone: boolean
  getValues: UseFormGetValues<TransferWizardFormValues>
  domains: string
  newdomains: string
  newPropertySave: string
  companyList: SearchableSelectOption[]
  companyListOld: SearchableSelectOption[]
  propertyList: SearchableSelectOption[]
  removestaffList: PropertyStaffRow[]
  transferstaffList: PropertyStaffRow[]
  addedstaffList: PropertyStaffRow[]
  commitstaffList: PropertyStaffRow[]
  commitDone: boolean
  verifyDone: boolean
  empDone: boolean
  primaryContactSet: boolean
  residentDone: boolean
  residentcount: number
  applicantcount: number
  overrideCommitList?: PropertyStaffRow[]
  flagOverrides?: TransferProgressFlagOverrides
}): TransferProgressJson {
  const {
    progressData,
    selectedTab,
    infoDone,
    getValues,
    domains,
    newdomains,
    newPropertySave,
    companyList,
    companyListOld,
    propertyList,
    removestaffList,
    transferstaffList,
    addedstaffList,
    commitstaffList,
    commitDone,
    verifyDone,
    empDone,
    primaryContactSet,
    residentDone,
    residentcount,
    applicantcount,
    overrideCommitList,
    flagOverrides,
  } = params

  const effectiveCommitDone = flagOverrides?.commitDone ?? commitDone
  const effectiveVerifyDone = flagOverrides?.verifyDone ?? verifyDone
  const effectiveEmpDone = flagOverrides?.empDone ?? empDone
  const effectiveInfoDone = flagOverrides?.infoDone ?? infoDone
  const effectiveNewPropertySave = flagOverrides?.newPropertySave ?? newPropertySave
  const effectiveResidentDone = flagOverrides?.residentDone ?? residentDone
  const effectiveResidentcount = flagOverrides?.residentcount ?? residentcount
  const effectiveApplicantcount = flagOverrides?.applicantcount ?? applicantcount

  const pd: TransferProgressJson = { ...progressData }
  if (selectedTab === 'propertyinfo') {
    if (!pd.propertyinfo) pd.propertyinfo = {}
    pd.propertyinfo.infodone = effectiveInfoDone
    pd.propertyinfo.info = getValues() as unknown as Record<string, unknown>
    pd.propertyinfo.domains = domains
    pd.propertyinfo.newdomains = newdomains
    pd.propertyinfo.newPropertySave = effectiveNewPropertySave
    const fv = getValues()
    if (fv.fk_company_id && companyList.length) {
      const sel = companyList.find((c) => c.value === fv.fk_company_id)
      if (sel) pd.propertyinfo.companylist = [{ value: fv.fk_company_id, label: sel.label }]
    }
    if (fv.old_company_id && companyListOld.length) {
      const sel = companyListOld.find((c) => c.value === fv.old_company_id)
      if (sel) pd.propertyinfo.companylistOld = [{ value: fv.old_company_id, label: sel.label }]
    }
    if (fv.property_id && propertyList.length) {
      const sel = propertyList.find((c) => c.value === fv.property_id)
      if (sel) pd.propertyinfo.propertylist = [{ value: fv.property_id, label: sel.label }]
    }
  }
  if (selectedTab === 'contactsinfo') {
    if (!pd.contactsinfo) pd.contactsinfo = {}
    pd.contactsinfo.removelist = removestaffList
    pd.contactsinfo.transferlist = transferstaffList
    pd.contactsinfo.addlist = addedstaffList
    pd.contactsinfo.commitstaffList = overrideCommitList ?? commitstaffList
    pd.contactsinfo.commitdone = effectiveCommitDone
    pd.contactsinfo.verifydone = effectiveVerifyDone
    pd.contactsinfo.empdone = effectiveEmpDone
    pd.contactsinfo.primarycontactset = primaryContactSet
  }
  if (selectedTab === 'residentinfo') {
    if (!pd.residentinfo) pd.residentinfo = {}
    pd.residentinfo.residentdone = effectiveResidentDone
    pd.residentinfo.residentcount = effectiveResidentcount
    pd.residentinfo.applicantcount = effectiveApplicantcount
  }
  return pd
}
